import { noop } from "lodash";
import { memo, useMemo } from "react";

import type { AccountDetails } from "@/apis/gql/types";
import {
  AccountForm,
  type FormValues as AccountFormValues,
} from "@/components/shared/form/lineItems/account";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useGetAllAccounts } from "@/queries/accounts/get-all";

export enum SpecialFilterValue {
  Unknown = "unknown",
}

type Props = {
  lineItemIds: string[];
  accountIds: (string | null)[];
  tooltipDescription?: {
    name: string;
    number: string;
    justification: string;
  }[];
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  onAccountChange: (value: AccountFormValues) => Promise<void> | void;
  onSuccess?: (value: string | null) => void;
  onError?: (error: Error) => void;
  readOnly?: boolean | string | React.ReactNode;
  disabled?: boolean | string | React.ReactNode;
};

type AccountsExplanationProps = {
  accounts: { name: string; number: string; justification: string }[];
};

const AccountsExplanation = ({ accounts }: AccountsExplanationProps) => {
  return (
    <div className="tooltip-maxwidth whitespace-normal text-wrap">
      {accounts.map(({ name, number, justification }) => (
        <div key={name} className="mb-2">
          <b>
            {name}
            <span>{` (${number})`}</span>
          </b>
          <br />
          <span>{justification}</span>
        </div>
      ))}
    </div>
  );
};

type MultipleAccountsProps = {
  accounts: (AccountDetails | undefined)[];
  tooltipDescription: {
    name: string;
    number: string;
    justification: string;
  }[];
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
};

const MultipleAccounts = ({
  accounts,
  className,
  tooltipDescription,
}: MultipleAccountsProps) => {
  const accountNames = accounts.map((a) => a?.accountName).join(", ");
  const accountNumbers = accounts.map((a) => a?.accountNo).join(", ");

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild className={cn("rounded", className)}>
          <Button
            variant="ghost"
            className={cn(
              "items-center whitespace-nowrap flex justify-between w-full overflow-hidden p-2 h-11",
            )}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="flex flex-col overflow-hidden">
              <span className="text-ellipsis overflow-hidden">
                {accountNames}
              </span>
              <span className="text-gray-500 text-xs text-left text-ellipsis overflow-hidden">
                {accountNumbers}
              </span>
            </div>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <AccountsExplanation accounts={tooltipDescription} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

type SingleAccountProps = {
  accounts: AccountDetails[];
  lineItemIds: string[];
  selectedAccounts: (AccountDetails | undefined)[];
  tooltipDescription: {
    name: string;
    number: string;
    justification: string;
  }[];
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  onAccountChange: (value: AccountFormValues) => Promise<void> | void;
  onError: (error: Error) => void;
  onSuccess?: (value: string | null) => void;
  readOnly?: boolean | string | React.ReactNode;
  disabled?: boolean | string | React.ReactNode;
};

const SingleAccount = ({
  accounts,
  readOnly,
  disabled,
  className,
  lineItemIds,
  onAccountChange,
  onSuccess,
  onError,
  selectedAccounts,
  tooltipDescription,
}: SingleAccountProps) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger className="w-full">
          <AccountForm
            readOnly={readOnly}
            initialValues={{
              accountId: selectedAccounts[0]?.accountId ?? null,
              lineItemIds,
            }}
            disabled={disabled}
            accounts={accounts}
            onSubmit={onAccountChange}
            onError={onError}
            onSuccess={onSuccess}
            allowUnknownAccount
            fullWidth={false}
            variant="ghost"
            className={className}
          />
        </TooltipTrigger>
        <TooltipContent>
          <AccountsExplanation accounts={tooltipDescription} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const AccountsCell = memo(
  ({
    readOnly = false,
    disabled = false,
    lineItemIds,
    accountIds,
    onAccountChange = noop,
    onError = noop,
    onSuccess = noop,
    tooltipDescription = [],
    className,
  }: Props) => {
    const { accounts } = useGetAllAccounts();

    const currentAccounts = useMemo(() => {
      if (!accounts) return [];

      return accountIds.map((accountId) =>
        accounts.find((a) => a.accountId === accountId),
      );
    }, [accountIds, accounts]);

    if (currentAccounts.length > 1) {
      return (
        <MultipleAccounts
          accounts={currentAccounts}
          tooltipDescription={tooltipDescription}
          className={className}
        />
      );
    }

    return (
      <SingleAccount
        accounts={accounts ?? []}
        lineItemIds={lineItemIds}
        selectedAccounts={currentAccounts}
        onAccountChange={onAccountChange}
        onSuccess={onSuccess}
        onError={onError}
        tooltipDescription={tooltipDescription}
        className={className}
        readOnly={readOnly}
        disabled={disabled}
      />
    );
  },
);
